import { forwardRef, type InputHTMLAttributes } from 'react'
import { motion } from 'framer-motion'

interface OptionCardProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string
  description?: string
  icon?: React.ReactNode
  selected?: boolean
}

export const OptionCard = forwardRef<HTMLInputElement, OptionCardProps>(
  ({ label, description, icon, selected, className = '', ...props }, ref) => {
    return (
      <motion.label
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        className={`
          relative flex items-start p-4 border-2 rounded-xl cursor-pointer transition-all duration-200
          ${selected 
            ? 'border-aquatic-500 bg-aquatic-50 shadow-md' 
            : 'border-gray-200 bg-white hover:border-aquatic-300 hover:bg-aquatic-50/50'
          }
          ${className}
        `}
      >
        <input
          ref={ref}
          type="radio"
          className="sr-only"
          {...props}
        />
        {icon && (
          <div className={`
            flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center mr-4
            ${selected ? 'bg-aquatic-500 text-white' : 'bg-gray-100 text-gray-600'}
          `}>
            {icon}
          </div>
        )}
        <div className="flex-1">
          <span className={`
            block text-base font-semibold
            ${selected ? 'text-aquatic-900' : 'text-gray-900'}
          `}>
            {label}
          </span>
          {description && (
            <span className="block text-sm text-gray-500 mt-1">
              {description}
            </span>
          )}
        </div>
        {selected && (
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="absolute top-3 right-3 w-6 h-6 bg-aquatic-500 rounded-full flex items-center justify-center"
          >
            <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
          </motion.div>
        )}
      </motion.label>
    )
  }
)

OptionCard.displayName = 'OptionCard'
